import { useCallback, useEffect, useMemo, useState } from 'react';

import type { SupportedLanguageValue } from '@ballerine/common';
import { SUPPORTED_LANGUAGES } from '@ballerine/common';

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions -- interface augmentation
  interface Window {
    google?: {
      translate?: {
        TranslateElement: new (options: unknown, id: string) => unknown;
      };
    };
    googleTranslateElementInit?: () => void;
  }
}

export type LanguageOption = {
  label: string;
  value: string;
};

export const GOOGLE_TRANSLATE_SCRIPT_ID = 'google-translate-script';
export const GOOGLE_TRANSLATE_ELEMENT_ID = 'google_translate_element';
export const GOOGLE_TRANSLATE_COOKIE = 'googtrans';

// Element.js intermittently inits into a broken state (constructor returns a
// populated internal-state object but injects no DOM, leaving the Select
// permanently disabled — ONC-41). Empirically verified that re-calling
// TranslateElement against the same window.google.translate runtime does NOT
// recover; only a fresh <script> load does. If the combo doesn't render
// within RELOAD_AFTER_MS, we surgically drop the corrupted runtime and load
// element.js again. Capped at MAX_TOTAL_LOAD_ATTEMPTS *total* loads (initial
// + recovery reloads), so 3 means initial + up to 2 reloads.
const COMBO_POLL_INTERVAL_MS = 250;
const RELOAD_AFTER_MS = 3000;
const MAX_TOTAL_LOAD_ATTEMPTS = 3;

export const LANGUAGE_OPTIONS: readonly LanguageOption[] = SUPPORTED_LANGUAGES;

export type LanguageValue = SupportedLanguageValue;

export const DEFAULT_LANGUAGE: LanguageValue = SUPPORTED_LANGUAGES[0].value;

const getPossibleCookieDomains = (): string[] => {
  if (typeof window === 'undefined') {
    return [''];
  }

  const hostname = window.location.hostname;
  const segments = hostname.split('.');
  const domains = new Set<string>(['', hostname, `.${hostname}`]);

  for (let i = 1; i < segments.length; i++) {
    const candidate = segments.slice(i).join('.');
    domains.add(candidate);
    domains.add(`.${candidate}`);
  }

  return Array.from(domains);
};

const getPossibleCookiePaths = (): string[] => {
  if (typeof window === 'undefined') {
    return ['/'];
  }

  const pathname = window.location.pathname || '/';

  if (pathname === '/' || pathname === '') {
    return ['/'];
  }

  const paths = new Set<string>(['/']);
  const segments = pathname.split('/').filter(Boolean);
  let current = '';

  segments.forEach(segment => {
    current += `/${segment}`;
    paths.add(current);
    paths.add(`${current}/`);
  });

  return Array.from(paths);
};

const getLanguageFromCookie = (
  defaultLanguage: LanguageValue,
  languages: readonly LanguageOption[],
): LanguageValue => {
  if (typeof document === 'undefined') {
    return defaultLanguage;
  }

  const cookie = document.cookie
    .split('; ')
    .find(row => row.startsWith(`${GOOGLE_TRANSLATE_COOKIE}=`));

  if (!cookie) {
    return defaultLanguage;
  }

  const value = decodeURIComponent(cookie.split('=')[1] ?? '');
  const parts = value.split('/').filter(Boolean);
  const cookieLanguage = parts.at(-1);

  if (!cookieLanguage) {
    return defaultLanguage;
  }

  return languages.some(({ value: optionValue }) => optionValue === cookieLanguage)
    ? (cookieLanguage as LanguageValue)
    : defaultLanguage;
};

const clearGoogleTranslateCookies = () => {
  if (typeof document === 'undefined') {
    return;
  }

  const cookieBase = `${GOOGLE_TRANSLATE_COOKIE}=; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  const domains = getPossibleCookieDomains();
  const paths = getPossibleCookiePaths();

  paths.forEach(path => {
    domains.forEach(domain => {
      const domainPart = domain ? `; domain=${domain}` : '';
      document.cookie = `${cookieBase}; path=${path}${domainPart}`;
    });
  });
};

type UseGoogleTranslateLanguageSelectorOptions = {
  languages?: readonly LanguageOption[];
  defaultLanguage?: LanguageValue;
  protectedTerms?: string[];
  enabled?: boolean;
  initialLanguage?: LanguageValue;
};

export const useGoogleTranslateLanguageSelector = ({
  languages = LANGUAGE_OPTIONS,
  defaultLanguage = DEFAULT_LANGUAGE,
  protectedTerms = [],
  enabled = true,
  initialLanguage,
}: UseGoogleTranslateLanguageSelectorOptions = {}) => {
  const [isTranslateReady, setIsTranslateReady] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<LanguageValue>(
    () => initialLanguage ?? getLanguageFromCookie(defaultLanguage, languages),
  );
  const normalizedProtectedTerms = useMemo(() => {
    const unique = new Set(
      protectedTerms.map(term => term.trim()).filter((term): term is string => Boolean(term)),
    );

    return Array.from(unique);
  }, [protectedTerms]);

  const googleLanguageList = useMemo(
    () =>
      languages.filter(language => language.value !== defaultLanguage).map(({ value }) => value),
    [defaultLanguage, languages],
  );

  const loadTranslateElement = useCallback(() => {
    if (!enabled) {
      setIsTranslateReady(false);

      return;
    }

    let comboPollTimeout: number | null = null;
    let cancelled = false;
    let elapsedPolling = 0;
    let loadAttempts = 0;

    function buildTranslateElement() {
      if (!window.google?.translate?.TranslateElement) {
        return;
      }

      // Don't clobber an already-rendered combo (StrictMode double-invoke,
      // concurrent effect re-run after a successful first init).
      if (document.querySelector('.goog-te-combo')) {
        return;
      }

      const container = document.getElementById(GOOGLE_TRANSLATE_ELEMENT_ID);

      if (container) {
        container.innerHTML = '';
      }

      new window.google.translate.TranslateElement(
        {
          pageLanguage: 'en',
          autoDisplay: false,
          includedLanguages: googleLanguageList.join(','),
        },
        GOOGLE_TRANSLATE_ELEMENT_ID,
      );
    }

    function reloadWidget() {
      // Surgically drop only window.google.translate so other window.google.*
      // APIs (e.g. analytics) keep working. The `delete` operator can throw
      // on a frozen descriptor — fall back to assigning undefined. The next
      // element.js load re-populates the namespace via its own
      // `window.google = window.google || {}; window.google.translate = ...`,
      // so this null state is short-lived.
      try {
        delete window.google?.translate;
      } catch {
        if (window.google) {
          window.google.translate = undefined as unknown as NonNullable<
            typeof window.google.translate
          >;
        }
      }

      const container = document.getElementById(GOOGLE_TRANSLATE_ELEMENT_ID);

      if (container) {
        container.innerHTML = '';
      }

      document.getElementById(GOOGLE_TRANSLATE_SCRIPT_ID)?.remove();

      loadScript();
    }

    function waitForCombo() {
      if (cancelled) {
        return;
      }

      if (document.querySelector<HTMLSelectElement>('.goog-te-combo')) {
        setIsTranslateReady(true);

        return;
      }

      elapsedPolling += COMBO_POLL_INTERVAL_MS;

      if (elapsedPolling >= RELOAD_AFTER_MS) {
        if (loadAttempts >= MAX_TOTAL_LOAD_ATTEMPTS) {
          // Give up rather than reload the page or loop forever. Select stays
          // disabled; user sees the bug but at least nothing else is destroyed.
          return;
        }

        reloadWidget();

        return;
      }

      comboPollTimeout = window.setTimeout(waitForCombo, COMBO_POLL_INTERVAL_MS);
    }

    function handleScriptReady() {
      if (cancelled) {
        return;
      }

      buildTranslateElement();
      waitForCombo();
    }

    function loadScript() {
      if (cancelled) {
        return;
      }

      loadAttempts += 1;
      elapsedPolling = 0;
      window.googleTranslateElementInit = handleScriptReady;

      if (window.google?.translate?.TranslateElement) {
        handleScriptReady();

        return;
      }

      if (!document.getElementById(GOOGLE_TRANSLATE_SCRIPT_ID)) {
        const script = document.createElement('script');
        script.id = GOOGLE_TRANSLATE_SCRIPT_ID;
        script.src =
          'https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
        script.async = true;
        document.body.appendChild(script);
      }
    }

    loadScript();

    return () => {
      cancelled = true;

      if (comboPollTimeout) {
        window.clearTimeout(comboPollTimeout);
      }
    };
  }, [enabled, googleLanguageList]);

  useEffect(loadTranslateElement, [loadTranslateElement]);

  useEffect(() => {
    if (!enabled || !isTranslateReady) {
      return;
    }

    const combo = document.querySelector<HTMLSelectElement>('.goog-te-combo');

    if (!combo) {
      return;
    }

    const googleValue = selectedLanguage === defaultLanguage ? '' : selectedLanguage;

    if (combo.value === googleValue) {
      return;
    }

    combo.value = googleValue;
    combo.dispatchEvent(new Event('change', { bubbles: true }));

    if (googleValue === '') {
      document.cookie = 'googtrans=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;';
    }
  }, [defaultLanguage, isTranslateReady, selectedLanguage]);

  useEffect(() => {
    if (!enabled || typeof document === 'undefined') {
      return;
    }

    if (!normalizedProtectedTerms.length) {
      return;
    }

    // Wrapping terms in `.notranslate` spans is only meaningful while Google
    // Translate is actively translating. When the user is on the default
    // language, nothing is being translated — skip the DOM walk entirely.
    // When they switch to a non-default language, this effect re-runs,
    // does a one-time full-body pass, and attaches the observer.
    if (selectedLanguage === defaultLanguage) {
      return;
    }

    const PROTECTED_ATTR = 'data-translation-protected';
    // Never walk/wrap inside editable regions. The user is actively mutating
    // them (typing/pasting), and injecting <span> wrappers into a contentEditable
    // can confuse ProseMirror/tiptap and cause correctness issues on top of
    // the performance cost of processing every input event.
    // `[contenteditable]:not([contenteditable="false"])` covers every editable
    // form: `contenteditable`, `contenteditable=""`, `contenteditable="true"`,
    // and `contenteditable="plaintext-only"`. We still list `.ProseMirror`
    // explicitly so readonly editors (which set contenteditable="false") are
    // also skipped — injecting spans into them can still confuse the editor's
    // reconciliation on state changes.
    const SKIP_SELECTOR = `[contenteditable]:not([contenteditable="false"]), .ProseMirror, [${PROTECTED_ATTR}]`;

    const wrapMatchesWithProtectedSpan = (node: Node, term: string) => {
      if (node.nodeType !== Node.TEXT_NODE || !node.textContent?.length) {
        return;
      }

      const parentElement = node.parentElement;

      if (!parentElement) {
        return;
      }

      if (parentElement.closest(SKIP_SELECTOR)) {
        return;
      }

      const textContent = node.textContent;
      const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`(${escapedTerm})`, 'gi');
      let match: RegExpExecArray | null;
      let lastIndex = 0;
      let hasMatch = false;
      const fragment = document.createDocumentFragment();

      while ((match = regex.exec(textContent)) !== null) {
        hasMatch = true;

        if (match.index > lastIndex) {
          fragment.appendChild(document.createTextNode(textContent.slice(lastIndex, match.index)));
        }

        const protectedSpan = document.createElement('span');
        protectedSpan.textContent = match[0];
        protectedSpan.classList.add('notranslate');
        protectedSpan.setAttribute('translate', 'no');
        protectedSpan.setAttribute(PROTECTED_ATTR, 'true');

        fragment.appendChild(protectedSpan);

        lastIndex = match.index + match[0].length;
      }

      if (!hasMatch) {
        return;
      }

      if (lastIndex < textContent.length) {
        fragment.appendChild(document.createTextNode(textContent.slice(lastIndex)));
      }

      parentElement.replaceChild(fragment, node);
    };

    const isInSkipZone = (node: Node): boolean => {
      const element =
        node.nodeType === Node.ELEMENT_NODE ? (node as Element) : (node as ChildNode).parentElement;

      return !!element?.closest(SKIP_SELECTOR);
    };

    const processRoot = (root: Node) => {
      if (!root.isConnected) {
        return;
      }

      // Early bail: if the root itself lives inside (or is) an editor or a
      // previously-protected subtree, we have nothing to do.
      if (isInSkipZone(root)) {
        return;
      }

      normalizedProtectedTerms.forEach(term => {
        const lowerTerm = term.toLowerCase();

        // TreeWalker with SHOW_TEXT only visits descendants; if the root itself
        // is a text node, handle it directly.
        if (root.nodeType === Node.TEXT_NODE) {
          const text = root.textContent ?? '';

          if (!text.trim()) {
            return;
          }

          if (text.toLowerCase().includes(lowerTerm)) {
            wrapMatchesWithProtectedSpan(root, term);
          }

          return;
        }

        const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
          acceptNode: node => {
            if (!node.textContent?.trim()) {
              return NodeFilter.FILTER_REJECT;
            }

            if (node.parentElement?.closest(SKIP_SELECTOR)) {
              return NodeFilter.FILTER_REJECT;
            }

            return node.textContent.toLowerCase().includes(lowerTerm)
              ? NodeFilter.FILTER_ACCEPT
              : NodeFilter.FILTER_REJECT;
          },
        });

        const matches: Node[] = [];

        while (walker.nextNode()) {
          matches.push(walker.currentNode);
        }

        matches.forEach(node => {
          wrapMatchesWithProtectedSpan(node, term);
        });
      });
    };

    // One-time initial pass over the whole body so pre-rendered content
    // gets wrapped. Subsequent mutations are processed scoped to their
    // added subtrees via the observer below.
    processRoot(document.body);

    const observer = new MutationObserver(records => {
      for (const record of records) {
        for (const added of record.addedNodes) {
          processRoot(added);
        }
      }
    });

    observer.observe(document.body, { subtree: true, childList: true });

    return () => {
      observer.disconnect();
    };
  }, [normalizedProtectedTerms, selectedLanguage, defaultLanguage, enabled]);

  useEffect(() => {
    if (!enabled) {
      setSelectedLanguage(defaultLanguage);

      return;
    }

    if (initialLanguage) {
      setSelectedLanguage(initialLanguage);

      return;
    }

    setSelectedLanguage(getLanguageFromCookie(defaultLanguage, languages));
  }, [defaultLanguage, enabled, initialLanguage, languages]);

  const clearTranslationState = useCallback(() => {
    if (!enabled) {
      return;
    }

    clearGoogleTranslateCookies();
    setSelectedLanguage(defaultLanguage);

    if (typeof document !== 'undefined') {
      const combo = document.querySelector<HTMLSelectElement>('.goog-te-combo');

      if (combo) {
        combo.value = '';
        combo.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }, [defaultLanguage, enabled]);

  const resetToDefaultLanguage = useCallback(() => {
    clearTranslationState();
    window.location.reload();
  }, [clearTranslationState]);

  useEffect(() => {
    if (!enabled || !isTranslateReady) {
      return;
    }

    // Google Translate manipulates the DOM in a way that breaks the app. This is a workaround to prevent the app from breaking. i.e. table sorting throws an error.
    if (typeof Node === 'function' && Node.prototype) {
      // @ts-ignore
      const originalRemoveChild = Node.prototype.removeChild;
      // @ts-ignore
      Node.prototype.removeChild = function (child) {
        if (child.parentNode !== this) {
          if (console) {
            console.error('Cannot remove a child from a different parent', child, this);
          }

          return child;
        }

        return originalRemoveChild.apply(
          this,
          // @ts-ignore
          // eslint-disable-next-line prefer-rest-params -- arguments is a valid argument
          arguments,
        );
      };

      // @ts-ignore
      const originalInsertBefore = Node.prototype.insertBefore;
      // @ts-ignore
      Node.prototype.insertBefore = function (newNode, referenceNode) {
        if (referenceNode && referenceNode.parentNode !== this) {
          if (console) {
            console.error(
              'Cannot insert before a reference node from a different parent',
              referenceNode,
              this,
            );
          }

          return newNode;
        }

        return originalInsertBefore.apply(
          this,
          // @ts-ignore
          // eslint-disable-next-line prefer-rest-params -- arguments is a valid argument
          arguments,
        );
      };
    }
  }, [enabled, isTranslateReady]);

  return {
    languages,
    selectedLanguage,
    isTranslateReady: enabled ? isTranslateReady : false,
    setSelectedLanguage,
    translateElementId: GOOGLE_TRANSLATE_ELEMENT_ID,
    defaultLanguage,
    clearTranslationState,
    resetToDefaultLanguage,
  };
};
